import React, { useEffect } from 'react';
import { Box, CardContent, Typography } from '@mui/material';
import { alpha } from '@mui/system';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { listOrganisationDailyUpdates } from '../../../../redux/slices/daily-updates';
import { RoleName } from '../../../../types/user';
import {
	ModernCard,
	CardHeader,
	ProgressContainer,
	ModernProgress,
	COLORS
} from '../../../../pages/Organisation/DailyUpdates/utils';
import RegistrarSkeleton from '../../atoms/DailyUpdates/RegistrarSkeleton';

const CustomStatusBadge = ({ color, label }: { color: string; label: number | string }) => (
	<Box
		sx={{
			borderRadius: '6px',
			backgroundColor: alpha(color, 0.15),
			color: color,
			fontWeight: 600,
			fontSize: '0.75rem',
			height: '24px',
			padding: '0 8px',
			display: 'inline-flex',
			alignItems: 'center',
			justifyContent: 'center'
		}}
	>
		{label}
	</Box>
);

// Define color mapping for registration statuses
const REGISTRATION_STATUS_COLORS = {
	pending: COLORS.warning,
	approved: COLORS.success,
	declined: COLORS.error
};

const RegistrarDU = () => {
	const dispatch = useAppDispatch();
	const [registrarDU, organisation, user] = useAppSelector((state) => [
		state.dailyUpdates.dailyUpdates?.[RoleName.Registrar],
		state.organisation.current,
		state.user.currentUser
	]);

	const organisationId = organisation?.id || user?.roles?.find((r) => r.organisationId)?.organisationId;

	const existingStudentsTotalCount =
		(registrarDU?.existingStudents?.pending ?? 0) +
		(registrarDU?.existingStudents?.approved ?? 0) +
		(registrarDU?.existingStudents?.declined ?? 0);

	const newStudentsTotalCount =
		(registrarDU?.newStudents?.pending ?? 0) +
		(registrarDU?.newStudents?.approved ?? 0) +
		(registrarDU?.newStudents?.declined ?? 0);

	useEffect(() => {
		organisationId &&
			user?.id &&
			dispatch(
				listOrganisationDailyUpdates({
					organisationId,
					roleName: RoleName.Registrar,
					status: null,
					dailyUpdates: true,
					lastLoggedOut: localStorage.getItem(`lastLogout_${user?.id}`) || undefined
				})
			);
	}, [dispatch, organisationId, user?.id]);

	if (!registrarDU) {
		return <RegistrarSkeleton />;
	}

	return (
		<Box
			sx={{
				p: 3,
				maxWidth: '1400px',
				margin: '0 auto',
				backgroundColor: alpha(COLORS.neutral, 0.5),
				borderRadius: '24px'
			}}
		>
			{/* Stats Overview Cards */}
			<Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
				{/* Existing Students Card */}
				<ModernCard sx={{ flex: 1 }}>
					<CardHeader>
						<Typography variant="h6" sx={{ fontWeight: 600, color: COLORS.text }}>
							Existing Students
						</Typography>
						<Typography variant="body2" sx={{ color: COLORS.textLight }}>
							{existingStudentsTotalCount} total updates
						</Typography>
					</CardHeader>
					<CardContent sx={{ p: 3 }}>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
							{(['pending', 'approved', 'declined'] as const).map((key) => {
								const count = registrarDU.existingStudents[key];
								const label = key.charAt(0).toUpperCase() + key.slice(1);
								return (
									<Box key={key}>
										<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
											<Typography variant="body2" sx={{ color: COLORS.textLight }}>
												{label}
											</Typography>
											<CustomStatusBadge color={REGISTRATION_STATUS_COLORS[key]} label={count} />
										</Box>
										<ProgressContainer>
											<ModernProgress
												variant="determinate"
												value={
													existingStudentsTotalCount > 0
														? (count / existingStudentsTotalCount || 0) * 100
														: 0
												}
												barColor={REGISTRATION_STATUS_COLORS[key]}
											/>
										</ProgressContainer>
									</Box>
								);
							})}
						</Box>
					</CardContent>
				</ModernCard>

				{/* New Students Card */}
				<ModernCard sx={{ flex: 1 }}>
					<CardHeader>
						<Typography variant="h6" sx={{ fontWeight: 600, color: COLORS.text }}>
							New Students
						</Typography>
						<Typography variant="body2" sx={{ color: COLORS.textLight }}>
							{newStudentsTotalCount} total updates
						</Typography>
					</CardHeader>
					<CardContent sx={{ p: 3 }}>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
							{(['pending', 'approved', 'declined'] as const).map((key) => {
								const count = registrarDU.newStudents[key];
								const label = key.charAt(0).toUpperCase() + key.slice(1);
								return (
									<Box key={key}>
										<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
											<Typography variant="body2" sx={{ color: COLORS.textLight }}>
												{label}
											</Typography>
											<CustomStatusBadge color={REGISTRATION_STATUS_COLORS[key]} label={count} />
										</Box>
										<ProgressContainer>
											<ModernProgress
												variant="determinate"
												value={
													newStudentsTotalCount > 0
														? (count / newStudentsTotalCount || 0) * 100
														: 0
												}
												barColor={REGISTRATION_STATUS_COLORS[key]}
											/>
										</ProgressContainer>
									</Box>
								);
							})}
						</Box>
					</CardContent>
				</ModernCard>
			</Box>
		</Box>
	);
};

export default RegistrarDU;
